import { useEffect, useState } from 'react';
import { useNavigate, Link } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { loginSchema, type LoginFormData } from '@/features/auth/schemas/login.schema';
import { useLoginMutation, useSwitchAppMutation } from '@/features/auth/queries/auth.queries';
import { useAuthStore } from '@/shared/store/auth.store';
import { useDocumentTitle } from '@/shared/hooks/use-document-title';
import { Loader2, Building2, ArrowRight } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import type { AplicacionDto } from '@/shared/types/tenant';

export function LoginPage(): React.ReactElement {
  useDocumentTitle('Iniciar Sesión');
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const setActiveApp = useAuthStore(state => state.setActiveApp);
  const loginMutation = useLoginMutation();
  const switchAppMutation = useSwitchAppMutation();
  const [pendingApps, setPendingApps] = useState<AplicacionDto[] | null>(null);

  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  useEffect(() => {
    if (isAuthenticated && !pendingApps) {
      navigate({ to: '/admin' });
    }
  }, [isAuthenticated, navigate, pendingApps]);

  function onSubmit(data: LoginFormData): void {
    loginMutation.mutate(data, {
      onSuccess: user => {
        toast.success('Acceso concedido');
        const apps = user.aplicaciones ?? [];

        if (apps.length <= 1) {
          // 0 apps (platform admin) or 1 app: auto-select and go
          const singleApp = apps[0];
          if (singleApp) {
            setActiveApp(singleApp);
            // Also call switch-app API so the backend sets the correct token
            switchAppMutation.mutate(
              { appCodigo: singleApp.codigo },
              {
                onSuccess: () => navigate({ to: '/admin' }),
                onError: () => navigate({ to: '/admin' }),
              }
            );
          } else {
            navigate({ to: '/admin' });
          }
        } else {
          // Multiple apps: show selection screen
          setPendingApps(apps);
        }
      },
      onError: error => {
        toast.error(error.message || 'Error al iniciar sesión');
      },
    });
  }

  function handleAppSelect(app: AplicacionDto): void {
    switchAppMutation.mutate(
      { appCodigo: app.codigo },
      {
        onSuccess: () => {
          setPendingApps(null);
          navigate({ to: '/admin' });
        },
        onError: error => {
          toast.error(error.message || 'Error al seleccionar aplicación');
        },
      }
    );
  }

  // App selection screen (shown after login when user has multiple apps)
  if (pendingApps && pendingApps.length > 1) {
    return (
      <div className="from-primary/5 via-background to-secondary/5 flex min-h-screen items-center justify-center bg-linear-to-br px-4 py-12">
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="bg-primary/20 absolute top-20 left-10 h-72 w-72 animate-pulse rounded-full mix-blend-multiply blur-xl filter" />
          <div className="bg-secondary/20 absolute top-40 right-10 h-72 w-72 animate-pulse rounded-full mix-blend-multiply blur-xl filter delay-1000" />
          <div className="bg-accent/30 absolute -bottom-8 left-1/2 h-72 w-72 animate-pulse rounded-full mix-blend-multiply blur-xl filter delay-2000" />
        </div>

        <div className="relative w-full max-w-lg">
          <div className="border-border/50 bg-card/70 shadow-strong relative overflow-hidden rounded-2xl border backdrop-blur-xl">
            <div className="from-card/40 via-card/20 pointer-events-none absolute inset-0 bg-linear-to-br to-transparent" />
            <div className="from-primary/5 to-secondary/5 pointer-events-none absolute inset-0 bg-linear-to-tl via-transparent" />
            <div className="from-primary via-secondary to-primary absolute top-0 right-0 left-0 h-1 bg-linear-to-r" />

            <div className="relative p-8 sm:p-10">
              <div className="mb-8 text-center">
                <div className="from-primary to-secondary shadow-medium mb-4 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-linear-to-br">
                  <Building2 className="h-8 w-8 text-white" />
                </div>
                <h1 className="text-foreground mb-2 text-2xl font-black">
                  Selecciona una{' '}
                  <span className="from-primary to-secondary bg-linear-to-r bg-clip-text text-transparent">
                    aplicación
                  </span>
                </h1>
                <p className="text-muted-foreground text-sm">
                  Tienes acceso a varias aplicaciones. Elige con cuál deseas trabajar.
                </p>
              </div>

              <div className="space-y-3">
                {pendingApps
                  .sort((a, b) => a.orden - b.orden)
                  .map(app => (
                    <button
                      key={app.id}
                      onClick={() => handleAppSelect(app)}
                      disabled={switchAppMutation.isPending}
                      className="border-border hover:border-primary/50 hover:bg-primary/5 group flex w-full items-center gap-4 rounded-xl border p-4 text-left transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <div className="from-primary/10 to-secondary/10 group-hover:from-primary/20 group-hover:to-secondary/20 flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-linear-to-br transition-colors duration-200">
                        <Building2 className="text-primary h-6 w-6" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-foreground truncate text-sm font-semibold">
                          {app.nombre}
                        </p>
                        <p className="text-muted-foreground truncate text-xs">
                          {app.organizacionNombre}
                        </p>
                      </div>
                      <ArrowRight className="text-muted-foreground group-hover:text-primary h-4 w-4 shrink-0 transition-colors duration-200" />
                    </button>
                  ))}
              </div>

              {switchAppMutation.isPending && (
                <div className="mt-4 flex items-center justify-center gap-2">
                  <Loader2 className="text-primary h-4 w-4 animate-spin" />
                  <span className="text-muted-foreground text-sm">Cargando...</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="from-primary/5 via-background to-secondary/5 flex min-h-screen items-center justify-center bg-linear-to-br px-4 py-12">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="bg-primary/20 absolute top-20 left-10 h-72 w-72 animate-pulse rounded-full mix-blend-multiply blur-xl filter" />
        <div className="bg-secondary/20 absolute top-40 right-10 h-72 w-72 animate-pulse rounded-full mix-blend-multiply blur-xl filter delay-1000" />
        <div className="bg-accent/30 absolute -bottom-8 left-1/2 h-72 w-72 animate-pulse rounded-full mix-blend-multiply blur-xl filter delay-2000" />
      </div>

      <div className="relative w-full max-w-md">
        <div className="border-border/50 bg-card/70 shadow-strong relative overflow-hidden rounded-2xl border backdrop-blur-xl">
          <div className="from-card/40 via-card/20 pointer-events-none absolute inset-0 bg-linear-to-br to-transparent" />
          <div className="from-primary/5 to-secondary/5 pointer-events-none absolute inset-0 bg-linear-to-tl via-transparent" />

          <div className="from-primary via-secondary to-primary absolute top-0 right-0 left-0 h-1 bg-linear-to-r" />

          <div className="relative p-8 sm:p-10">
            <div className="mb-8 text-center">
              <div className="from-primary to-secondary shadow-medium mb-4 inline-flex h-20 w-20 transform items-center justify-center rounded-2xl bg-linear-to-br transition-all duration-300 hover:scale-105">
                <img
                  src="/apeps-isotipo.png"
                  alt="APEPS"
                  className="h-12 w-12 object-contain brightness-0 invert"
                />
              </div>
              <h1 className="text-foreground mb-2 text-3xl font-black">
                Bienvenido a{' '}
                <span className="from-primary to-secondary bg-linear-to-r bg-clip-text text-transparent">
                  APEPS
                </span>
              </h1>
              <p className="text-muted-foreground text-sm">
                Ingresa tus credenciales para continuar
              </p>
            </div>

            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="space-y-2">
                <label htmlFor="email" className="text-foreground block text-sm font-semibold">
                  Usuario
                </label>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 z-10 flex items-center pl-3">
                    <svg
                      className="text-muted-foreground h-5 w-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  </div>
                  <Input
                    id="email"
                    type="text"
                    placeholder="Ingresa tu usuario"
                    className="pl-10"
                    data-testid="auth.login.email"
                    {...form.register('email')}
                  />
                </div>
                {form.formState.errors.email && (
                  <p className="text-destructive text-sm">{form.formState.errors.email.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor="password" className="text-foreground block text-sm font-semibold">
                  Contraseña
                </label>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 z-10 flex items-center pl-3">
                    <svg
                      className="text-muted-foreground h-5 w-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                    </svg>
                  </div>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Ingresa tu contraseña"
                    className="pl-10"
                    data-testid="auth.login.password"
                    {...form.register('password')}
                  />
                </div>
                {form.formState.errors.password && (
                  <p className="text-destructive text-sm">
                    {form.formState.errors.password.message}
                  </p>
                )}
              </div>

              <div>
                <Button
                  type="submit"
                  disabled={loginMutation.isPending}
                  className="w-full"
                  data-testid="auth.login.submit"
                >
                  {loginMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Iniciando sesión...
                    </>
                  ) : (
                    'Iniciar Sesión'
                  )}
                </Button>
              </div>
            </form>

            <div className="mt-6 text-center">
              <Link
                to="/"
                className="text-muted-foreground hover:text-primary inline-flex items-center text-sm transition-colors duration-300"
              >
                <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
                Volver al sitio web
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
